import React, { FC, Key, ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Guide } from "@mapmycustomers/shared/types/entity";
import { Modal } from "@mapmycustomers/ui";

import Table from "@app/component/Table";
import GuidesQuickSearch from "@app/scene/onboarding/components/GuidesQuickSearch";
import { getGuides } from "@app/scene/onboarding/store";
import { RootState } from "@app/store/rootReducer";

import styles from "./AssignGuidesModal.module.scss";
import useColumns from "./useColumns";

const messages = defineMessages({
  ok: {
    id: "onboarding.users.assignModal.ok",
    defaultMessage: "Assign",
    description: "Ok button title the Assign Guides modal on the Users Guides page",
  },
  title: {
    id: "onboarding.users.assignModal",
    defaultMessage: "Assign Guides",
    description: "Title of the Assign Guides modal on the Users Guides page",
  },
});

export interface AssignGuidesModalProps {
  allowEmpty?: boolean;
  onChange?: (guideIds: Guide["id"][]) => void;
  onHide: () => void;
  subTitle?: ReactNode;
  value?: Guide["id"][];
}

interface Props extends AssignGuidesModalProps {
  guides: Guide[];
}

const AssignGuidesModal: FC<Props> = ({
  allowEmpty,
  guides,
  onChange,
  onHide,
  subTitle,
  value,
}) => {
  const intl = useIntl();

  const columns = useColumns(intl);

  const [selectedGuideIds, setSelectedGuideIds] = useState<Guide["id"][]>(value ?? []);
  const handleSelectionChange = useCallback(
    (keys: Key[]) => setSelectedGuideIds?.(keys as Guide["id"][]),
    []
  );

  const handleOk = useCallback(() => {
    onChange?.(selectedGuideIds);
  }, [selectedGuideIds, onChange]);

  const [quickSearch, setQuickSearch] = useState<string>("");
  const filteredGuides = useMemo(() => {
    const q = quickSearch.trim().toLowerCase();
    if (!q) {
      return guides;
    }
    return guides.filter((guide) => guide.displayName.includes(q) || guide.description.includes(q));
  }, [guides, quickSearch]);

  return (
    <Modal
      className={styles.container}
      okButtonProps={{
        disabled: !selectedGuideIds.length && !allowEmpty,
      }}
      okText={intl.formatMessage(messages.ok)}
      onCancel={onHide}
      onOk={handleOk}
      open
      title={intl.formatMessage(messages.title)}
      width="clamp(30rem, 90vw, 80rem)"
    >
      <div className={styles.toolbar}>
        <p className={styles.subTitle}>{subTitle}</p>
        <GuidesQuickSearch
          className={styles.search}
          onChange={setQuickSearch}
          value={quickSearch}
        />
      </div>
      <Table<Guide>
        className={styles.table}
        columns={columns}
        dataSource={filteredGuides}
        pagination={false}
        rowKey="id"
        rowSelection={{
          onChange: handleSelectionChange,
          selectedRowKeys: selectedGuideIds,
        }}
        scrollable
        showBorder
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  guides: getGuides(state),
});

export default connect(mapStateToProps)(AssignGuidesModal);
